<template>
    <div class="ma-3">
        <!-- add dialog -->
        <v-dialog v-model="dialog" width="350">
            <v-card>
                <v-card-title class="headline">{{$t('TransferDocument.create-document')}}</v-card-title>
                <v-card-text class="pb-0">
                    <bee-date-picker
                      context="materials_journal"
                      :action-name="actions.CREATE"
                      :label="$t('MaterialsJournalPage.journal-date')"
                      v-model="date"
                      :rules="[rules.date]"
                    ></bee-date-picker>
                </v-card-text>
                <v-card-actions class="ms-3">
                    <v-btn color="greenDarken1" text @click="addDocument" :loading="loading" >
                        {{$t('MaterialsJournalPage.create')}}
                    </v-btn>
                    <v-btn color="redDarken1" text @click="dialog = false" >
                        {{$t('MaterialsJournalPage.cancel')}}
                    </v-btn>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- delete dialog -->
		<bee-dialog
			v-model="deleteDialog"
			max-width="350"
			:persistent="true"
			:title="$t('TransferDocument.delete-document')"
			:message="$t('TransferDocument.are-you-sure-you-want-to-delete-the-document')"
            :loading-confirm="loading"
			@click:confirm="confirmDeleteDocument"
			@click:close="entryId = null; deleteDialog = false;"
		></bee-dialog>
        <!-- filter and buttons -->
        <v-row class="my-2">
            <v-col md="auto" cols="12" align-self="center">
                <slot name="breadcrumbs" :item="true"></slot>
            </v-col>
    
            <!-- no -->
            <v-col cols="12" sm="6" md="2" class="d-flex" align-self="center">
				<v-btn icon @click="prevJournal()">
					<v-icon>{{ $i18n.locale === 'en' ? 'mdi-menu-left' : 'mdi-menu-right'}}</v-icon>
				</v-btn>
                <v-text-field
					v-model.number="boundNo"
					label="رقم السند"
					type=number
					dense
					hide-details
					:disabled="loading"
				/>
				<v-btn icon @click="nextJournal()">
					<v-icon>{{$i18n.locale === 'en' ? 'mdi-menu-right' : 'mdi-menu-left'}}</v-icon>
				</v-btn>
			</v-col>
            <!--  -->
            <v-col cols="auto" align-self="center" class="py-0">
                <v-btn @click="dialog = true" color="addingColor white--text">
                    <v-icon> mdi-plus </v-icon>
                    {{ $t('add') }}
                </v-btn>

                <v-btn
                	v-if="!isPosted"
                	color="deletingColor white--text"
                	class="mx-2 py-0"
                	@click="deleteDialog = true"
                >
                    <v-icon> mdi-delete </v-icon>
                    {{ $t('buttons.delete') }}
                </v-btn>
            </v-col>

            <v-spacer />
            <v-col cols="auto" xl="3" class="d-flex justify-space-between">
                <v-btn 
                    :color="!isPosted ? 'blueGray white--text' : 'deletingColor white--text'"
                    height="60"
                    @click="!isPosted ? postedBound() : unPosted()"
                >   
                    {{ !isPosted ? $t('save') : $t('edit') }}
                </v-btn>

                <v-btn 
                    :color="!isPosted ? 'postedColor white--text' : 'deletingColor white--text'"
                    height="60"
                    class="mx-2"
                    depressed
                >
                    <v-icon> mdi-email </v-icon>
                    {{ !isPosted ? $t('JournalsPage.non-posted') : $t('JournalsPage.posted') }}
                </v-btn>
            </v-col>
        </v-row>
        <bee-handy-smart-table
            v-model="tableInputs"
            ref="table"
            :headers="headers"
            :items="entries !== null ? entries : []"
            dense
            zebra
            :show-add-row="!isPosted && bonds.length > 0"
            :items-per-page="-1"
            :reset-edit.sync="resetEdit"
			editable-rows
            pagination-on-scroll
            :height="$vuetify.breakpoint.height - 200"
            hide-inputs-details
            hide-shown-rows-text
            stay-in-input-field
            :context-menu-items="contextMenuItems"
            :disable-context-menu="loading || isPosted"
            :scroll-to-row-expression="(item) => item.id === entryId || item.id === entries[entries.length - 1].id"
            :data-table-props="{ disableSort: true }"
            :disable-input-fields="loading"
            @keypress.enter="submitEntry()"
            @edit-cancelled="entryId = null"
			@context-menu-edit="setRowIndex"
			@context-menu-delete="({ item }) => deleteEntry(item.id)"
            @reached-last-input="submitEntry()"
            :loading="loading || submitLoading"
        >
            <template v-slot:input.source_warehouse_id="{ on, attr }">
                <v-autocomplete 
                    :items="tableInputs.target_warehouse_id ? warehouses.filter(c => c.id !== tableInputs.target_warehouse_id) : warehouses" 
                    item-text="name"
                    item-value="id"
                    v-on="on"
                    v-bind="attr"
                    @change="tableInputs.target_material_id = null;"
                />
            </template>

            <template v-slot:input.source_material_id="{ on, attr }">
                <v-autocomplete 
                    :items="materials.filter(c => c.warehouse_id === tableInputs.source_warehouse_id)"
                    item-text="name"
                    item-value="id"
                    v-on="on"
                    v-bind="attr"
                    @change="
                        tableInputs.target_material_id = null;
                        tableInputs.target_warehouse_id ? getMaterialLinked() : null;
                    "
                />
            </template>

            <template v-slot:input.target_warehouse_id="{ on, attr }">
                <v-autocomplete 
                    :items="warehouses.filter(c => c.id !== tableInputs.source_warehouse_id)"
                    item-text="name"
                    item-value="id"
                    v-on="on"   
                    v-bind="attr"
                    @change="
                        tableInputs.target_material_id = null;
                        getMaterialLinked();
                    "
                />
            </template>

            <template v-slot:input.target_material_id="{ on, attr }">
                <v-autocomplete 
                    :items="itemLinkedWithMaterials"
                    item-text="name"
                    item-value="id"
                    v-on="on"
                    v-bind="attr"
                    :loading="loadingItems"
                />
            </template>

            <template v-slot:input.unit="{ on, attr }">
                <v-autocomplete 
                    :items="unitSizes"
                    item-text="name"
                    item-value="id"
                    v-on="on"
                    v-bind="attr"
                />
            </template>

            <template v-slot:input.date="{ on, attr }">
                <bee-date-input
                    label=" "
                    fit-inputs
                    dense
                    input-format-order="DD-MM-YYYY"
                    v-on="on"
                    v-bind="attr"
                ></bee-date-input>
            </template>
        </bee-handy-smart-table>
    </div>
</template>

<!--  -->
<script>
import { mapState } from "vuex";
import rules from "../../../validation";
import actions from "../../../permissions/actions-names.js";
import moment from "moment";
export default {
    watch: {
        'tableInputs.source_warehouse_id'(val) {
            if(val) {
                this.materials.filter(c => c.warehouse_id === this.tableInputs.source_warehouse_id)
            }
        }
    },
    data() {
        return {
            loading: false,
            loadingItems: false,

            date: null,
            dialog: false,
            rules,
            actions,
            entries: [],
            entryId: null,
            tableInputs: {
                date: moment().format('YYYY-MM-DD')
            },
            rowEditIndex: null,
            isEditItem: null,
            resetEdit: false,
            boundNo: null,

            deleteDialog: false,
            isPosted: null,
            deleteEntryId: null,
            itemLinkedWithMaterials: [],
            submitLoading: false,

            bonds: []
        }
    },
    computed: {
        ...mapState({
            warehouses: state => state.warehouses.warehouses,
            materials: state => state.materials.materials,
            transactionBonds: state => state.transactionBonds.transactionBonds,
            unitSizes: state => state.unitSizes.unit_sizes,
        }),
        ...mapState("configs" , ["configs"]),
        contextMenuItems() {
			return [
				{
					title: this.$t('buttons.edit'),
					event: 'edit',
				},
				{
					title: this.$t('buttons.delete'),
					event: 'delete',
				},
			]
		},
        headers() {
            const headers = [
                {
                    text: this.$t('headers.source-warehouse'),
                    name: 'source_warehouse_id',
                    value: 'source_warehouse_id',
                    width: 180,
                    inputRules: [rules.rules.required],
                    setValueExpr: val => this.warehouses.find(c => c.id === val)?.name,
                    columnClass: 'px-2'
                },
                {
                    text: this.$t('headers.material'),
                    name: 'source_material_id',
                    value: 'source_material_id',
                    width: 240,
                    inputRules: [rules.rules.required],
                    setValueExpr: val => this.materials.find(c => c.id === val)?.name,
                    columnClass: 'px-2'
                },
                {
                    text: this.$t('headers.target-warehouse'),
                    name: 'target_warehouse_id',
                    value: 'target_warehouse_id',
                    width: 180,
                    inputRules: [rules.rules.required],
                    setValueExpr: val => this.warehouses.find(c => c.id === val)?.name,
                    columnClass: 'px-2'
                },
                {
                    text: this.$t('headers.material'),
                    name: 'target_material_id',
                    value: 'target_material_id',
                    width: 240,
                    inputRules: [rules.rules.required],
                    setValueExpr: val => this.materials.find(c => c.id === val)?.name,
                    columnClass: 'px-2'
                },
                {
                    text: this.$t('headers.quantity'),
                    name: 'quantity',
                    value: 'quantity.quantity',
                    type: 'number-format',
                    width: 80,
                    inputRules: [rules.rules.required],
                },
                {
                    text: this.$t('manufacturingBond.headers.unit'),
                    name: 'unit',
                    value: 'quantity.unit_size_id',
                    width: 100,
                    inputRules: [rules.rules.required],
                    setValueExpr: (val) => this.unitSizes.find(c => c.id === val)?.name,
                    columnClass: 'px-2'
                },
                {
                    text: this.$t('headers.notes'),
                    name: 'notes',
                    value: 'notes',
                    columnClass: 'px-2'
                },
                {
                    text: this.$t('headers.date'),
                    name: 'date',
                    value: 'date',
                    inputRules: [rules.rules.required],
                    columnClass: 'px-2'
                }
            ]
            return headers
        },

        boundNoList() {
            return this.bonds.map(c => c.transaction_no);
        }
    },

    methods: {
        moment,
        addDocument() {
            this.loading = true
            this.$store.dispatch('transactionBonds/create', { date: this.date }).then(({ data }) => {
                this.boundNo = data.transaction_no
                this.entries = []
                this.$router.push({
                    name: 'transfer-document',
                    query: { id: data.id }
                });
                this.fetchAllBounds();
            }).finally(() => {
                this.loading = false
                this.dialog = false
            })
        },

        submitEntry() {
            if (!this.$refs.table.validate()) return;

            this.submitLoading = true
            const actionName = this.entryId ? 'updateEntry' : 'createEntry'
            this.$store.dispatch(`transactionBonds/${actionName}`, { 
                entryId: this.entryId ? this.entryId : null,
                source_warehouse_id: this.tableInputs.source_warehouse_id,
                target_warehouse_id: this.tableInputs.target_warehouse_id, 
                source_material_id: this.tableInputs.source_material_id, 
                target_material_id :this.tableInputs.target_material_id, 
                transaction_bond_id : +this.$route.query.id,  
                quantity: {
                    quantity: Number(this.tableInputs.quantity),
                    unit_size_id: this.tableInputs.unit
                },
                notes: this.tableInputs.notes, 
                date: this.tableInputs.date
            }).then(() => {
                this.$refs.table.resetInputsFocus();
                this.getAllEntries().finally(() => {
                    this.submitLoading = false
                    this.$refs.table.resetInputs({ date: new Date().toISOString().slice(0, 10) })
                })
            }).catch(() => {
                this.submitLoading = false;
            }).finally(() => {
                this.$refs.table.resetEdit()
                this.isEditItem = false
                this.entryId = null
            })
        },

        getAllEntries() {
            return this.$store.dispatch('transactionBonds/fetchEntries', { transactionBondId: this.$route.query.id }).then((data) => {
                this.entries = data.data.map((c) => ({ ...c, unitSizes: c.quantity.unit_size_id}))
            })
        },

        setRowIndex(rowData) {
			this.rowEditIndex = rowData.index;
			this.entryId = rowData.item.id;
			this.$nextTick(() => {
				this.$refs.table.resetInputsFocus();
                this.getMaterialLinked();
			})
		},

        deleteEntry(id) {
            this.loading = true
            this.$store.dispatch('transactionBonds/deleteEntry', {  
                transactionBondId: this.$route.query.id,
                entryId: id
            }).then(() => {
                this.getAllEntries()
            }).finally(() => {
                this.loading = false
                this.entryId = null
            })
        },

        fetchJournalByNo() {
            const journalId = this.bonds.find(c => c.transaction_no === this.boundNo)?.id;
            if (journalId && +this.$route.query.id !== journalId) {
                this.$router.push({
                    name: 'transfer-document',
                    query: { id: journalId }
                })
            }
            this.entries = [];
            
            this.loading = true
            this.$store.dispatch('transactionBonds/fetchJournalByNo', {
                transactionBondNo: this.boundNo
            }).then((data) => {
                this.entries = data.data.entries
                this.isPosted = data.data.is_posted 
            }).finally(() => {
                this.loading = false
            })
        },
        
        fetchAllBounds(isFirst = false) {
            this.$store.dispatch('transactionBonds/fetchMaterialFlow', { 
                fromDate: moment(this.configs.financial_file.start_date).format('YYYY-MM-DD'),
                toDate: moment(new Date().toISOString().substr(0, 10)).format('YYYY-MM-DD')
            }).then((data) => {
                this.bonds = data;

                if (data.length) {
                    if (isFirst && !this.$route.query.id) {
                        this.boundNo = this.bonds[this.bonds.length - 1].transaction_no;
                        this.$router.push({
                            name: 'transfer-document',
                            query: { id: this.bonds[this.bonds.length - 1].id }
                        })
                    } else if(this.$route.query.id) {
                        this.boundNo = this.bonds.find(c => c.id === +this.$route.query.id)?.transaction_no
                    }
                    this.fetchJournalByNo()
                } else {
                    this.boundNo = null;
                }
            })
        },
        confirmDeleteDocument() {
            this.loading = true
            this.$store.dispatch('transactionBonds/delete', {
                transactionBondId: this.$route.query.id
            }).then(() => {
                this.entries = [];
                this.$router.replace({ name: 'transfer-document' });
                this.fetchAllBounds(true);
            }).finally(() => {
                this.loading = false
                this.deleteDialog = false
            })
        },

        postedBound() {
            this.loading = true
            this.$store.dispatch('transactionBonds/posted', { transactionBondId: this.$route.query.id }).then(() => {
                this.isPosted = true
            }).finally(() => {
                this.loading = false
            })
        },

        unPosted() {
            this.loading = true
            this.$store.dispatch('transactionBonds/unPosted', { transactionBondId: this.$route.query.id }).then(() => {
                this.isPosted = false
            }).finally(() => {
                this.loading = false
            })
        },
        nextJournal() {
            const indexOfCurrentInvoice = this.boundNoList.indexOf(this.boundNo);
            if (indexOfCurrentInvoice !== this.boundNoList.length - 1) {
                this.boundNo = this.boundNoList[this.boundNoList.indexOf(this.boundNo) + 1]
                this.fetchJournalByNo()
            }
		},
		prevJournal() {
            const indexOfCurrentInvoice = this.boundNoList.indexOf(this.boundNo);
            if (![-1, 0].includes(indexOfCurrentInvoice)) {
                this.boundNo = this.boundNoList[indexOfCurrentInvoice - 1]
                this.fetchJournalByNo()
            }
		},

        getMaterialLinked() {
            if (this.tableInputs.source_material_id) {
                this.loadingItems = true
                this.$store.dispatch('warehouses/fetchMaterialLinkedWithMaterial', { 
                    materialId: this.tableInputs.source_material_id
                }).then((data) => {
                    this.itemLinkedWithMaterials = data.data.filter(c => c.warehouse_id === this.tableInputs.target_warehouse_id)
                }).finally(() => {
                    this.loadingItems = false
                })
            }
        },
    },

    created() {
        Promise.all([
            !this.warehouses.length ? this.$store.dispatch('warehouses/fetchAll') : null,
            // !this.materials.length ? 
            this.$store.dispatch('materials/fetchAll'),
            // : null,
            !this.unitSizes.length ? this.$store.dispatch("unitSizes/fetchAll") : null,
            this.fetchAllBounds(true)
        ]).finally(() => {
            this.$refs.table.resetInputs({ date: new Date().toISOString().slice(0, 10) })
        })
    }
}
</script>
